import { type ReactNode, type Ref, useRef } from "react";
import { useDebouncedSave, useServerDraft } from "../../lib/autosave";
import type { IssueDetail } from "../../types";
import { SectionHeader } from "../page";
import { Markdown } from "./markdown";
import { MarkdownEditor, normalizeMarkdown } from "./markdown-editor";
import { ShowMore } from "./show-more";

export function IssueTitleField({
  value,
  onChange,
  fieldRef,
}: {
  value: string;
  onChange: (value: string) => void;
  fieldRef?: Ref<HTMLTextAreaElement>;
}) {
  return (
    <textarea
      ref={fieldRef}
      value={value}
      rows={1}
      onChange={(event) => onChange(event.target.value.replace(/\n/g, " "))}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.preventDefault();
      }}
      placeholder="Issue title"
      aria-label="Title"
      // Reads as a heading until the pointer or focus lands on it, then shows a soft field.
      className="field-sizing-content -mx-1.5 w-[calc(100%+0.75rem)] max-w-none resize-none overflow-hidden rounded-md bg-transparent px-1.5 py-1 text-xl leading-[1.3] font-semibold tracking-[-0.018em] break-words transition-colors duration-(--motion-dur) outline-none placeholder:text-muted-foreground/60 hover:bg-muted/60 focus:bg-muted/60 focus:ring-1 focus:ring-border"
    />
  );
}

/** The static title and description for people who can see the issue but not edit it. */
function ReadOnlyIssue({
  issue,
  belowTitle,
  hideDescription,
}: {
  issue: IssueDetail;
  belowTitle?: ReactNode;
  hideDescription?: boolean;
}) {
  return (
    <div className="flex flex-col gap-5">
      <h1 className="py-1 text-xl leading-[1.3] font-semibold tracking-[-0.018em] break-words">
        {issue.title}
      </h1>
      {belowTitle}
      <section aria-label="Description" hidden={hideDescription}>
        <SectionHeader title="Description" className="mb-1" />
        {issue.description.trim() ? (
          <ShowMore>
            <Markdown>{issue.description}</Markdown>
          </ShowMore>
        ) : (
          <p className="text-sm text-muted-foreground">No description.</p>
        )}
      </section>
    </div>
  );
}

export function IssueEditor({
  issue,
  pending,
  saved,
  onSave,
  readOnly = false,
  belowTitle,
  hideDescription = false,
}: {
  issue: IssueDetail;
  pending: boolean;
  saved: boolean;
  onSave: (patch: { title?: string; description?: string }) => void;
  readOnly?: boolean;
  /** Rendered between the title and the description. */
  belowTitle?: ReactNode;
  /** Hidden rather than unmounted, so a description edit still saves while another tab is open. */
  hideDescription?: boolean;
}) {
  if (readOnly)
    return <ReadOnlyIssue issue={issue} belowTitle={belowTitle} hideDescription={hideDescription} />;
  return (
    <EditableIssue
      issue={issue}
      pending={pending}
      saved={saved}
      onSave={onSave}
      belowTitle={belowTitle}
      hideDescription={hideDescription}
    />
  );
}

function EditableIssue({
  issue,
  pending,
  saved,
  onSave,
  belowTitle,
  hideDescription,
}: {
  issue: IssueDetail;
  pending: boolean;
  saved: boolean;
  onSave: (patch: { title?: string; description?: string }) => void;
  belowTitle?: ReactNode;
  hideDescription: boolean;
}) {
  const [title, setTitle, markTitleSent] = useServerDraft(issue.title, trimTitle);
  const [description, setDescription, markDescriptionSent] = useServerDraft(
    issue.description,
    normalizeMarkdown,
  );
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const trimmedTitle = trimTitle(title);
  const normalizedDescription = normalizeMarkdown(description);
  const savedDescription = normalizeMarkdown(issue.description);

  useDebouncedSave(trimmedTitle, issue.title, (next) => {
    if (!next) return;
    markTitleSent(next);
    onSaveRef.current({ title: next });
  });
  useDebouncedSave(normalizedDescription, savedDescription, (next) => {
    markDescriptionSent(next);
    onSaveRef.current({ description: next });
  });

  const dirty =
    (trimmedTitle.length > 0 && trimmedTitle !== issue.title) || normalizedDescription !== savedDescription;
  const hint = pending || dirty ? "Saving…" : saved ? "Saved" : undefined;

  return (
    <div className="flex flex-col gap-5">
      <IssueTitleField value={title} onChange={setTitle} />
      {belowTitle}
      <div hidden={hideDescription}>
        <ShowMore expandOnFocus>
          <MarkdownEditor
            title="Description"
            initial={issue.description}
            value={description}
            onChange={setDescription}
            hint={hint}
          />
        </ShowMore>
      </div>
    </div>
  );
}

function trimTitle(value: string): string {
  return value.trim();
}
